import { useState } from "react";
import BackToTop from "./BackToTop";
import Footer from "./Footer";

const services = [
  {
    number: "01",
    kicker: "Cultural localization",
    title: "Make your brand understandable — not just visible.",
    paragraphs: [
      "Entering a new market requires more than translating content.",
      "We adapt messaging, campaigns, and voice so they align with how people think, not just how they read.",
    ],
    items: [
      "Content Localization",
      "Campaign Adaptation",
      "Voice & Audio Identity",
    ],
    tone: "red",
  },
  {
    number: "02",
    kicker: "Creative production",
    title: "Ideas only matter when they are executed with clarity.",
    paragraphs: [
      "We produce content from concept to final delivery with a focus on intent, precision, and communication impact.",
    ],
    items: [
      "Creative Direction",
      "Concept Development",
      "Scriptwriting",
      "Video Production",
      "Post Production",
      "UGC Content",
    ],
    tone: "ink",
  },
  {
    number: "03",
    kicker: "Brand identity",
    title: "A brand is a system of recognition.",
    paragraphs: [
      "Not a logo. Not a visual style. But a structured way people identify and remember you.",
      "We build identity systems designed to stay consistent as brands grow and expand.",
    ],
    items: [
      "Brand Identity",
      "Visual Systems",
      "Logo Design",
      "Motion Design",
      "Brand Guidelines",
    ],
    tone: "paper",
  },
  {
    number: "04",
    kicker: "Digital experiences",
    title: "Digital experiences built around people.",
    paragraphs: [
      "We design and develop websites and digital platforms that combine usability, performance, and scalability.",
    ],
    items: [
      "UX Strategy",
      "UI Design",
      "Website Development",
      "Digital Platforms",
    ],
    tone: "ink",
  },
  {
    number: "05",
    kicker: "AI solutions",
    title: "Technology should create more time for creativity.",
    paragraphs: [
      "We integrate artificial intelligence into creative workflows to accelerate production, simplify operations, and improve efficiency.",
    ],
    items: [
      "AI Creative Content",
      "Creative Automation",
      "Workflow Design",
      "AI Integration",
    ],
    tone: "paper",
  },
];

const TriangleFlow = ({ count = 9 }) => (
  <div className="triangle-flow" aria-hidden="true">
    {Array.from({ length: count }, (_, index) => (
      <span key={index} style={{ "--i": index }} />
    ))}
  </div>
);

const Home = () => {
  const [result, setResult] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    formData.append("access_key", "762ae7fc-b087-46d6-8351-a777b6389a51");

    const postForm = (mode = "cors") =>
      fetch("https://api.web3forms.com/submit", {
        method: "POST",
        mode,
        cache: "no-cache",
        headers: mode === "cors" ? { Accept: "application/json" } : undefined,
        body: formData,
      });

    const parseResponse = async (response) => {
      const text = await response.text();
      let data = null;
      try {
        data = JSON.parse(text);
      } catch {
        // response may not always be JSON
      }
      return { response, text, data };
    };

    try {
      const { response, text, data } = await parseResponse(await postForm());
      const isSuccess =
        response.ok &&
        (data?.success === true ||
          data?.success === "true" ||
          /success/i.test(text));
      const successMessage = "Thank you — your request has been received.";
      const errorMessage = data?.message || data?.error || text;

      if (isSuccess || response.type === "opaque") {
        setResult(successMessage);
        form.reset();
      } else {
        setResult(errorMessage || "Something went wrong. Please try again.");
      }
    } catch (error) {
      console.error("Web3Forms submit error:", error);
      if (error instanceof TypeError) {
        try {
          await postForm("no-cors");
          setResult("Thank you — your request has been received.");
          form.reset();
          return;
        } catch (fallbackError) {
          console.error("Web3Forms no-cors fallback failed:", fallbackError);
        }
      }
      setResult(`Submission failed. ${error?.message || "Please try again."}`);
    }
  };

  return (
    <main>
      <section className="hero" id="home">
        <div className="hero-light" aria-hidden="true" />
        <div className="hero-grid">
          <div className="eyebrow">Independent creative agency · NL</div>
          <h1 data-reveal>
            Ideas don’t belong
            <br />
            to one place.
            <span>They evolve when they move.</span>
          </h1>
          <div className="hero-foot">
            <p>Strategy · Design · Production · Cultural intelligence</p>
            <a href="#about" className="scroll-cue">
              Scroll to move{" "}
              <span aria-hidden="true">
                <i className="fa-solid fa-arrow-down"></i>
              </span>
            </a>
          </div>
        </div>
      </section>

      <section className="intro page-section" id="about">
        <div className="intro-layout">
          <p className="section-label" data-reveal>
            Based in the Netherlands.
            <br />
            Built for everywhere.
          </p>
          <div className="intro-copy" data-reveal>
            <h2>
              Creative agency based in the Netherlands, helping brands grow
              across cultures.
            </h2>
            <div className="two-column-copy">
              <p>
                We work with organizations and ambitious brands to build
                clarity, relevance, and connection across different markets.
              </p>
              <p>
                Through strategy, design, production, and cultural intelligence,
                we shape brands and experiences that hold meaning wherever they
                appear.
              </p>
            </div>
          </div>
        </div>
        <TriangleFlow />
      </section>

      <section className="manifesto page-section">
        <div className="manifesto-top">
          <p className="section-label" data-reveal>
            Our point of view
          </p>
          <h2 data-reveal>
            Culture is not a layer.
            <br />
            <em>It is the system.</em>
          </h2>
        </div>
        <div className="manifesto-copy" data-reveal>
          <p>
            Brands don’t fail because they are not creative enough. They fail
            because they stop making sense when they enter new contexts.
          </p>
          <p>
            Every market has its own logic — how people read messages, how they
            trust, how they decide.
          </p>
          <p className="stacked-lines">
            Translation is not enough.
            <br />
            Design is not enough.
            <br />
            Production is not enough.
          </p>
          <p className="red-line">Relevance is the real work.</p>
          <p>We build brands that can move without losing meaning.</p>
        </div>
      </section>

      <section className="what-we-do page-section" id="what-we-do-id">
        <div className="work-heading" data-reveal>
          <p className="section-label">What we do</p>
          <h2>
            We build meaning
            <br />
            that <span>travels.</span>
          </h2>
        </div>
        <div className="work-detail" data-reveal>
          <p>
            Our work sits at the intersection of culture, communication, and
            creative execution.
          </p>
          <p>
            We don’t start with formats.
            <br />
            <strong>We start with understanding.</strong>
          </p>
        </div>
        <div className="orbit-graphic" aria-hidden="true">
          <div className="orbit orbit-one" />
          <div className="orbit orbit-two" />
          <span className="orbit-core" />
        </div>
      </section>

      {/* Our work moved to a dedicated page (Work.js) */}

      <section className="services" id="services">
        <div className="services-intro page-section">
          <p className="section-label" data-reveal>
            Capabilities
          </p>
          <h2 data-reveal>
            One partner.
            <br />
            <span>Many ways to move.</span>
          </h2>
          <TriangleFlow count={13} />
        </div>
        {services.map((service) => (
          <article
            id={service.number === "01" ? "service-01" : undefined}
            className={`service-panel service-panel--${service.tone}`}
            key={service.number}
          >
            <div className="service-number">{service.number}</div>
            <div className="service-main" data-reveal>
              <p className="service-kicker">{service.kicker}</p>
              <h3>{service.title}</h3>
              <div className="service-paragraphs">
                {service.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
              </div>
            </div>
            <div className="service-list" data-reveal>
              <p>Services</p>
              <ol>
                {service.items.map((item, index) => (
                  <li key={item}>
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    {item}
                  </li>
                ))}
              </ol>
            </div>
          </article>
        ))}
      </section>

      <section className="why page-section">
        <p className="section-label" data-reveal>
          Why us
        </p>
        <div className="why-layout">
          <h2 data-reveal>
            Creative thinking
            <br />
            with cultural
            <br />
            <em>understanding.</em>
          </h2>
          <p data-reveal>
            Our multidisciplinary team brings together creative strategy,
            design, production, technology, and cultural insight. This
            combination allows us to build work that is not only visually
            compelling, but contextually relevant to the audiences it serves.
          </p>
        </div>
        <div className="why-ticker" aria-hidden="true">
          CREATIVE · CULTURAL · RELEVANT ·{" "}
        </div>
      </section>

      <section className="contact page-section" id="contact">
        <div className="contact-heading" data-reveal>
          <p className="section-label">Have a project in mind?</p>
          <h2>
            Creative work travels further when it <span>belongs.</span>
          </h2>
          <p className="section-label2">
            Let’s build brands that connect beyond borders.
          </p>
        </div>
        <form className="contact-form" onSubmit={handleSubmit} data-reveal>
          <label>
            <span>Name</span>
            <input
              type="text"
              name="name"
              placeholder="Your full name"
              required
            />
          </label>
          <label>
            <span>Email</span>
            <input
              type="email"
              name="email"
              placeholder="you@company.com"
              required
            />
          </label>
          <label>
            <span>Your request</span>
            <textarea
              name="request"
              placeholder="Tell us what you want to build"
              rows="3"
              required
            />
          </label>
          <button type="submit">
            Send request <span aria-hidden="true">↗</span>
          </button>
          {result && (
            <p className="form-success" role="status">
              {result}
            </p>
          )}
        </form>
      </section>

      <Footer backLinkHref="#home" backLinkText="Back to top" />

      <BackToTop href="#home" label="Back to top" />
    </main>
  );
};

export default Home;
